import { useExamCenter } from '@exam-center/admin-exam-center-details';
import { OrderType, useSearchAssignableExamCoursesQuery } from '@exam-center/graphql';
import { NetworkStatus } from '@shared/apollo';
import { Flex, Loading, NoRowsOverlay, SearchInput, Text } from '@shared/components';
import { useMemo, useState } from 'react';

import {
  AllExamCourseAssignedOverlay,
  ExamCenterCourseAssignmentDataGrid,
  type ExamCenterCourseAssignmentDataGridProps,
} from '../../components';
import { useSearchAssignableExamCourseSearchParams } from '../../hooks';
import { mapExamCenterCourseData } from '../../utils/';

interface ExamCenterCourseAssignmentListProps {
  onSelectRow?: (selectedMembers: string[]) => void;
}

export default function ExamCenterCourseAssignmentList(
  props: Readonly<ExamCenterCourseAssignmentListProps>,
) {
  const { onSelectRow } = props;
  const [selectedExamCourseCounts, setSelectedExamCourseCounts] = useState(0);

  const {
    examCenter: { code: examCenterCode },
  } = useExamCenter();

  const [{ search, page, pageSize }, { changePageNumber, changePageSize, changeSearch }] =
    useSearchAssignableExamCourseSearchParams();

  const {
    data: assignableExamCoursesData,
    loading: isLoading,
    networkStatus,
  } = useSearchAssignableExamCoursesQuery({
    variables: {
      searchAssignableExamCoursesInput: {
        examCenterCode,
        filter: {
          searchText: {
            value: search,
            fields: ['name'],
          },
        },
        paging: {
          currentPage: page,
          pageSize,
        },
        order: {
          type: OrderType.Asc,
          field: 'examId',
        },
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const assignableExamCourses = useMemo(
    () =>
      assignableExamCoursesData?.ecSearchAssignableExamCourses.examCourses.map(
        mapExamCenterCourseData,
      ) ?? [],
    [assignableExamCoursesData],
  );

  const isInitialLoading = networkStatus === NetworkStatus.loading;

  const isAllCourseAssigned =
    search.trim() === '' && !isLoading && assignableExamCourses.length === 0;

  const totalAssignableExamCourses =
    assignableExamCoursesData?.ecSearchAssignableExamCourses.total ?? 0;

  const handleSelectRow: ExamCenterCourseAssignmentDataGridProps['onRowSelectionModelChange'] = (
    selectedRows,
  ) => {
    onSelectRow?.(selectedRows as string[]);
    setSelectedExamCourseCounts(selectedRows.length);
  };

  if (isInitialLoading) {
    return <Loading />;
  }

  if (isAllCourseAssigned) {
    return <AllExamCourseAssignedOverlay />;
  }

  return (
    <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(6) }}>
      <SearchInput placeholder='ค้นหาหลักสูตร...' onChange={changeSearch} />
      <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
        <Text variant='h4'>เลือกหลักสูตรทดสอบ ({selectedExamCourseCounts})</Text>
        <ExamCenterCourseAssignmentDataGrid
          rows={assignableExamCourses}
          loading={isLoading}
          onRowSelectionModelChange={handleSelectRow}
          hideFooter={!totalAssignableExamCourses}
          slots={{
            noRowsOverlay: NoRowsOverlay,
          }}
          slotProps={{
            footer: {
              total: totalAssignableExamCourses,
              pagination: {
                page,
                count: totalAssignableExamCourses,
                rowsPerPage: pageSize,
                onPageChange: (_, page) => {
                  changePageNumber(page);
                },
                onRowsPerPageChange: (event) => {
                  changePageSize(parseInt(event.target.value));
                },
              },
            },
          }}
        />
      </Flex>
    </Flex>
  );
}
